import React from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../../app/store/slices/uiSlice";

// Field names that should not contain numbers (name fields)
const NAME_FIELDS = ['name', 'firstName', 'lastName', 'first_name', 'last_name', 'contactPerson', 'contact_person'];

// Check if field is a name field
const isNameField = (fieldName) => {
  const lowerName = fieldName?.toLowerCase() || '';
  return NAME_FIELDS.some(nf => lowerName.includes(nf.toLowerCase()));
};

export default function FormInput({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
  disabled = false,
  error = "",
  className = "",
  hideLabel = true,
  maxLength,
  ...props
}) {
  const darkMode = useSelector(selectDarkMode);

  // Handle input change with validation
  const handleChange = (e) => {
    let newValue = e.target.value;

    // Phone validation: only digits, max 10 characters
    if (type === "tel") {
      // Remove all non-digit characters
      newValue = newValue.replace(/\D/g, '');
      // Limit to 10 digits
      if (newValue.length > 10) {
        newValue = newValue.slice(0, 10);
      }
      e.target.value = newValue;
    }

    // Number validation: enforce min/max values
    if (type === "number" && newValue !== '') {
      const numValue = parseFloat(newValue);
      const minVal = props.min !== undefined ? parseFloat(props.min) : null;
      const maxVal = props.max !== undefined ? parseFloat(props.max) : null;

      // Prevent negative values if min is 0 or greater
      if (minVal !== null && numValue < minVal) {
        newValue = String(minVal);
        e.target.value = newValue;
      }
      if (maxVal !== null && numValue > maxVal) {
        newValue = String(maxVal);
        e.target.value = newValue;
      }
    }

    // Name field validation: no numbers allowed
    if (isNameField(name)) {
      // Remove all digits
      newValue = newValue.replace(/[0-9]/g, '');
      e.target.value = newValue;
    }

    onChange(e);
  };

  const baseClass = "w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 transition-colors";
  const borderClass = error
    ? "border-red-500 focus:ring-red-500"
    : value
      ? darkMode ? "border-green-500 focus:ring-green-500 bg-gray-700 text-white" : "border-green-500 focus:ring-green-500"
      : darkMode ? "border-gray-600 focus:ring-blue-500 bg-gray-700 text-white placeholder-gray-400" : "border-gray-300 focus:ring-blue-500";

  const disabledClass = disabled
    ? darkMode ? "bg-gray-800 cursor-not-allowed text-gray-400" : "bg-gray-100 cursor-not-allowed"
    : "";

  // Set maxLength for phone fields
  const inputMaxLength = type === "tel" ? 10 : maxLength;

  return (
    <div className="form-group">
      {!hideLabel && label && (
        <label htmlFor={name} className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-[#232526]'}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={inputMaxLength}
        className={`${baseClass} ${borderClass} ${disabledClass} ${className}`}
        {...props}
      />

      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
}
